import { Injectable } from "@angular/core";
import { EpubAnnotationsSerializerService } from "./epub-annotations-serializer.service";
import { DocumentManagerService } from "../document/document-manager.service";
import JSZip from "jszip";
import { AsgardDocEpub } from "src/app/core/documents/asgard-doc-epub";
import { IEpubAnnotations } from "./base-epub-annotation.service";
import { filter, take } from "rxjs";
import { MimeTypesEnum } from "src/app/utils/enums";

export interface IEpubAndAnnotationFiles {
    epubFile: File,
    annotationsJson: string
}

@Injectable({ providedIn: 'root' })
export class EpubFileService {
    private readonly ANNOTATIONS_FILE_NAME ='annotations.json';
    constructor(
        private readonly annotationSerializer: EpubAnnotationsSerializerService,
        private documentManager: DocumentManagerService
    ) { }

    public async exportWithAnnotations(): Promise<File> {
        if (!this.documentManager.currentDoc || !(this.documentManager.currentDoc instanceof AsgardDocEpub)) {
            throw Error('No current doc to export EPUB with annotations');
        }

        const annotations = await this.annotationSerializer.serialize();
        const currentDoc = this.documentManager.currentDoc as AsgardDocEpub;
        const file = currentDoc.epubViewerInstance?.epubFile;

        if (!file || !annotations) {
            throw Error('Failed to export EPUB with annotations');
        }

        const zip = new JSZip();
        zip.file(this.ANNOTATIONS_FILE_NAME, JSON.stringify(annotations));
        zip.file(file.name, file);

        const arrayBuffer = await zip.generateAsync({ type: 'arraybuffer' });
        const zipBlob = new Blob([arrayBuffer], { type: MimeTypesEnum.ApplicationBook });

        const filename = `${file.name.replace('.epub', '')}.alineabook`;
        return new File([zipBlob], filename, { type: MimeTypesEnum.ApplicationBook });
    }

    public async importFromZip(zipFile: File): Promise<IEpubAndAnnotationFiles> {
        const zip = await JSZip.loadAsync(zipFile);
        const annEntry = zip.file(this.ANNOTATIONS_FILE_NAME);

        if (!annEntry) throw new Error(this.ANNOTATIONS_FILE_NAME + ' not found in ZIP');
        const annotationsJson = await annEntry.async('string');

        const epubEntries = zip.file(/\.epub$/i);
        if (!epubEntries || epubEntries.length === 0) {
          throw new Error('No EPUB file found in ZIP');
        }
        const epubEntry = epubEntries[0];
        const epubFile = new File([await epubEntry.async('blob')], epubEntry.name, { type: 'application/epub+zip', lastModified: Date.now() });

        return { epubFile, annotationsJson };
    }

    public applyAnnotationsJson(annotationsJson: string, tabId: string) {
        const annotations: IEpubAnnotations = JSON.parse(annotationsJson);
        const sub = this.documentManager.currentDocumentChanged.subscribe(id => {
            if (tabId !== id) return;

            const currDoc = this.documentManager.currentDoc;
            if (!(currDoc instanceof AsgardDocEpub)) return;

            currDoc.isDocumentReady$.pipe(filter(ready => ready), take(1)).subscribe(_ => {
                const bookId = currDoc.epubViewerInstance?.readerPublication.getSourcePublication().getMetadata().getUniqueIdentifier()?.content.value;
                if (bookId !== annotations.bookId) return;

                this.annotationSerializer.deserialize(annotations);
                sub.unsubscribe();
            });
        });

    }
}